import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import {
  Check,
  CheckSquare,
  Copy,
  FileText,
  Loader2,
  MessageSquare,
  Sparkles,
} from 'lucide-react'
import { toast } from 'sonner'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Kbd } from '@/components/ui/kbd'
import { Textarea } from '@/components/ui/textarea'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { AiSparkleBadge } from '@/components/app/ai/AiSparkle'
import { usePages, useProjects } from '@/lib/db/rxdb/hooks'
import AuthAxios from '@/lib/api/axios/AuthAxios'
import { ENDPOINT_BASE } from '@/config/endpoints'
import useLoginStore from '@/store/LoginStore'

// Calls POST /api/v1.0/e/{slug}/ai/ask on the backend AiController. Renders the
// synthesised answer + numbered citations. Streaming is intentionally deferred —
// /ask returns the full response in one shot for now; we'll swap to SSE once the
// backend exposes a streaming variant.

type SourceType = 'Document' | 'WorkItem' | 'Comment'

type Citation = {
  marker: string
  sourceType: SourceType
  sourceId: string
  documentId?: string
  title: string
  chunkIndex: number
  score: number
  snippet: string
}

type AskResponse = {
  question: string
  answer: string
  model: string
  citations: Array<Citation>
}

function getErrorMessage(err: any, fallback: string) {
  return (
    err?.response?.data?.detail ||
    err?.response?.data?.title ||
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    fallback
  )
}

const EXAMPLE_QUESTION_KEYS = [
  'openTasks',
  'recentDecisions',
  'onboardingPages',
  'blockers',
] as const

const SOURCE_ICON: Record<SourceType, typeof FileText> = {
  Document: FileText,
  WorkItem: CheckSquare,
  Comment: MessageSquare,
}

const SOURCE_LABEL_KEY: Record<SourceType, 'page' | 'task' | 'comment'> = {
  Document: 'page',
  WorkItem: 'task',
  Comment: 'comment',
}

function markerNumber(marker: string, fallback: number) {
  const n = parseInt(marker.replace(/\D/g, ''), 10)
  return Number.isFinite(n) && n > 0 ? n : fallback
}

function jumpToCitation(n: number) {
  const el = document.getElementById(`ask-citation-${n}`)
  if (!el) return
  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  el.animate(
    [
      { boxShadow: '0 0 0 2px rgb(217 70 239 / 0.45)' },
      { boxShadow: '0 0 0 2px rgb(217 70 239 / 0)' },
    ],
    { duration: 1200 },
  )
}

// Citation chip: clickable [N] that scrolls + flashes the source card below.
function CitationChip({ n }: { n: number }) {
  return (
    <button
      type="button"
      onClick={() => jumpToCitation(n)}
      className="mx-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded bg-fuchsia-500/10 px-1 align-baseline text-[10px] font-semibold text-fuchsia-600 hover:bg-fuchsia-500/20 dark:text-fuchsia-400"
    >
      {n}
    </button>
  )
}

// Walk a string and turn [N] markers into citation chips, leaving other text intact.
// Used as the children renderer inside react-markdown text leaves.
function withCitations(text: string) {
  const parts = text.split(/(\[\d+\])/g)
  return parts.map((part, idx) => {
    const m = part.match(/^\[(\d+)\]$/)
    if (m) {
      return <CitationChip key={idx} n={parseInt(m[1] ?? '0', 10)} />
    }
    return <Fragment key={idx}>{part}</Fragment>
  })
}

// Apply citation-chip substitution to every text child of a Markdown element.
function citizeChildren(children: React.ReactNode): React.ReactNode {
  if (typeof children === 'string') return withCitations(children)
  if (Array.isArray(children)) {
    return children.map((c, i) =>
      typeof c === 'string' ? (
        <Fragment key={i}>{withCitations(c)}</Fragment>
      ) : (
        <Fragment key={i}>{c}</Fragment>
      ),
    )
  }
  return children
}

// react-markdown component overrides that:
//   1. Substitute [N] markers in text leaves with clickable citation chips
//   2. Use the existing dialog visual style (tight margins, no surprising fonts)
//   3. Open links in new tabs with rel=noreferrer (safe for model-emitted urls)
const markdownComponents = {
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="mb-2 last:mb-0">{citizeChildren(children)}</p>
  ),
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-semibold">{citizeChildren(children)}</strong>
  ),
  em: ({ children }: { children?: React.ReactNode }) => (
    <em className="italic">{citizeChildren(children)}</em>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="mb-2 ml-5 list-disc space-y-1 last:mb-0">{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="mb-2 ml-5 list-decimal space-y-1 last:mb-0">{children}</ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <li>{citizeChildren(children)}</li>
  ),
  code: ({ children }: { children?: React.ReactNode }) => (
    <code className="bg-muted rounded px-1 py-0.5 text-[0.85em]">{children}</code>
  ),
  pre: ({ children }: { children?: React.ReactNode }) => (
    <pre className="bg-muted my-2 overflow-x-auto rounded p-2 text-xs">{children}</pre>
  ),
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1 className="mt-2 mb-1 text-base font-semibold">{citizeChildren(children)}</h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="mt-2 mb-1 text-sm font-semibold">{citizeChildren(children)}</h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="mt-2 mb-1 text-sm font-semibold">{citizeChildren(children)}</h3>
  ),
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-fuchsia-600 underline-offset-2 hover:underline dark:text-fuchsia-400"
    >
      {citizeChildren(children)}
    </a>
  ),
  // GFM tables — narrow dialog so default HTML <table> looks broken without these.
  table: ({ children }: { children?: React.ReactNode }) => (
    <div className="my-2 overflow-x-auto">
      <table className="w-full border-collapse text-xs">{children}</table>
    </div>
  ),
  thead: ({ children }: { children?: React.ReactNode }) => (
    <thead className="border-b">{children}</thead>
  ),
  tbody: ({ children }: { children?: React.ReactNode }) => <tbody>{children}</tbody>,
  tr: ({ children }: { children?: React.ReactNode }) => (
    <tr className="border-b last:border-b-0">{children}</tr>
  ),
  th: ({ children }: { children?: React.ReactNode }) => (
    <th className="px-2 py-1 text-left font-semibold">{citizeChildren(children)}</th>
  ),
  td: ({ children }: { children?: React.ReactNode }) => (
    <td className="px-2 py-1 align-top">{citizeChildren(children)}</td>
  ),
}

function renderAnswer(answer: string) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={markdownComponents}
    >
      {answer}
    </ReactMarkdown>
  )
}

export interface AskWorkspaceDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Asked as soon as the dialog opens (⌘K's "Ask"). */
  initialQuestion?: string
}

export function AskWorkspaceDialog({ open, onOpenChange, initialQuestion }: AskWorkspaceDialogProps) {
  const { t } = useTranslation('common')
  const tr = (key: string) => t(`ai.askWorkspace.${key}`)
  const navigate = useNavigate()
  const workspaceSlug = useLoginStore((s) => s.workspaceSlug)
  const { pages } = usePages()
  const { projects } = useProjects()

  const [question, setQuestion] = useState('')
  const [askedQuestion, setAskedQuestion] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<AskResponse | null>(null)
  const [copied, setCopied] = useState(false)

  const pageMap = useMemo(() => {
    const m = new Map<string, NonNullable<typeof pages>[number]>()
    pages?.forEach((p) => m.set(p.id, p))
    return m
  }, [pages])

  const projectMap = useMemo(() => {
    const m = new Map<string, NonNullable<typeof projects>[number]>()
    projects?.forEach((p) => m.set(p.id, p))
    return m
  }, [projects])

  const ask = async (q: string) => {
    const trimmed = q.trim()
    if (!trimmed || !workspaceSlug) return
    setAskedQuestion(trimmed)
    setQuestion('')
    setLoading(true)
    setResult(null)
    try {
      const res = await AuthAxios.post<AskResponse>(
        `${ENDPOINT_BASE}/e/${workspaceSlug}/ai/ask`,
        { question: trimmed },
      )
      setResult(res.data)
    } catch (err: any) {
      toast.error(getErrorMessage(err, tr('askFailed')))
      setAskedQuestion('')
    } finally {
      setLoading(false)
    }
  }

  // ⌘K hands over a question: ask it once per opening.
  const askedInitialRef = useRef(false)
  useEffect(() => {
    if (!open) {
      askedInitialRef.current = false
      return
    }
    if (initialQuestion?.trim() && !askedInitialRef.current) {
      askedInitialRef.current = true
      void ask(initialQuestion)
    }
  }, [open, initialQuestion])

  const handleReset = () => {
    setQuestion('')
    setAskedQuestion('')
    setResult(null)
    setCopied(false)
  }

  const handleCopy = async () => {
    if (!result?.answer) return
    try {
      await navigator.clipboard.writeText(result.answer)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      toast.error(tr('copyFailed'))
    }
  }

  const handleCitationClick = (c: Citation) => {
    if (!workspaceSlug) return

    if (c.sourceType === 'WorkItem') {
      void navigate({
        to: '/$workspaceSlug/tasks/$taskId',
        params: { workspaceSlug, taskId: c.sourceId },
      })
      onOpenChange(false)
      return
    }

    const navigateToPage = (pageId: string) => {
      const page = pageMap.get(pageId)
      if (!page || !page.slug) return false
      const project = page.projectId ? projectMap.get(page.projectId) : undefined
      if (!project) return false
      void navigate({
        to: '/$workspaceSlug/projects/$projectKey/pages/$pageSlug',
        params: {
          workspaceSlug,
          projectKey: project.projectKey,
          pageSlug: page.slug,
        },
      })
      onOpenChange(false)
      return true
    }

    if (c.sourceType === 'Document' && navigateToPage(c.sourceId)) return
    if (c.sourceType === 'Comment' && c.documentId && navigateToPage(c.documentId)) return

    toast.info(tr('sourceNotFound'))
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleReset()
        onOpenChange(next)
      }}
    >
      <DialogContent className="gap-0 p-0 sm:max-w-2xl">
        <DialogHeader className="border-b px-6 pt-6 pb-4">
          <DialogTitle className="flex items-center gap-2.5 text-base">
            <AiSparkleBadge />
            {tr('title')}
          </DialogTitle>
          <DialogDescription>{tr('description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 border-b px-6 py-4">
          <Textarea
            placeholder={tr('placeholder')}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                void ask(question)
              }
            }}
            rows={3}
            disabled={loading}
            className="resize-none focus-visible:border-fuchsia-500/40 focus-visible:ring-fuchsia-500/30"
          />
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
              <Kbd>⌘</Kbd>
              <Kbd>Enter</Kbd>
              <span>{tr('submitHint')}</span>
            </span>
            <div className="flex items-center gap-2">
              {(result || askedQuestion) && (
                <Button variant="ghost" size="sm" onClick={handleReset}>
                  {tr('newQuestion')}
                </Button>
              )}
              <Button
                size="sm"
                onClick={() => void ask(question)}
                disabled={loading || !question.trim()}
                className="bg-gradient-to-br from-fuchsia-500 to-violet-500 text-white shadow-sm hover:from-fuchsia-600 hover:to-violet-600"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                    {tr('thinking')}
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-1.5 size-3.5" />
                    {tr('ask')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {!askedQuestion && (
          <div className="px-6 py-5">
            <div className="text-muted-foreground mb-2.5 text-[11px] font-semibold tracking-wide uppercase">
              {tr('tryAsking')}
            </div>
            <div className="flex flex-wrap gap-2">
              {EXAMPLE_QUESTION_KEYS.map((key) => {
                const q = tr(`examples.${key}`)
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => void ask(q)}
                    className="border-input hover:border-fuchsia-500/40 hover:bg-fuchsia-500/5 rounded-full border px-3 py-1.5 text-xs transition-colors"
                  >
                    {q}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {askedQuestion && (
          <div className="max-h-[60vh] overflow-y-auto">
            <div className="space-y-5 px-6 py-5">
              <div className="flex justify-end">
                <div className="max-w-[85%] rounded-2xl rounded-br-sm border border-fuchsia-500/20 bg-fuchsia-500/10 px-3.5 py-2.5 text-sm whitespace-pre-wrap">
                  {askedQuestion}
                </div>
              </div>

              {loading && (
                <div className="flex items-start gap-2.5">
                  <span className="bg-muted mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-full">
                    <Sparkles className="size-3.5 animate-pulse text-fuchsia-500" />
                  </span>
                  <div className="flex-1 space-y-2 pt-1">
                    <div className="bg-muted h-3 w-3/4 animate-pulse rounded" />
                    <div className="bg-muted h-3 w-full animate-pulse rounded" />
                    <div className="bg-muted h-3 w-5/6 animate-pulse rounded" />
                  </div>
                </div>
              )}

              {result && !loading && (
                <div className="space-y-4">
                  <div className="flex items-start gap-2.5">
                    <span className="mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-fuchsia-500 to-violet-500 text-white">
                      <Sparkles className="size-3.5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex items-center justify-between gap-2">
                        <span className="text-muted-foreground text-[11px] font-semibold tracking-wide uppercase">
                          {tr('answer')}
                        </span>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="size-7"
                                onClick={handleCopy}
                              >
                                {copied ? (
                                  <Check className="size-3.5 text-emerald-500" />
                                ) : (
                                  <Copy className="text-muted-foreground size-3.5" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {copied ? tr('copied') : tr('copyAnswer')}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <div className="text-sm leading-relaxed">
                        {renderAnswer(result.answer)}
                      </div>
                    </div>
                  </div>

                  {result.citations?.length > 0 && (
                    <div className="space-y-2 pl-8">
                      <div className="text-muted-foreground text-[11px] font-semibold tracking-wide uppercase">
                        {tr('sources')} · {result.citations.length}
                      </div>
                      <ul className="space-y-2">
                        {result.citations.map((c, i) => {
                          const Icon = SOURCE_ICON[c.sourceType] ?? FileText
                          const n = markerNumber(c.marker, i + 1)
                          return (
                            <li
                              key={`${c.sourceType}-${c.sourceId}-${c.chunkIndex}`}
                              id={`ask-citation-${n}`}
                              className="rounded-md transition-shadow"
                            >
                              <button
                                type="button"
                                onClick={() => handleCitationClick(c)}
                                className="group hover:border-fuchsia-500/40 hover:bg-fuchsia-500/5 w-full rounded-md border p-3 text-left transition-colors"
                              >
                                <div className="flex items-start gap-3">
                                  <span className="bg-muted text-muted-foreground group-hover:bg-fuchsia-500/15 mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded text-[10px] font-semibold group-hover:text-fuchsia-600 dark:group-hover:text-fuchsia-400">
                                    {n}
                                  </span>
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5">
                                      <Icon className="text-muted-foreground size-3.5 shrink-0" />
                                      <span className="text-muted-foreground text-[11px] tracking-wide uppercase">
                                        {tr(`sourceTypes.${SOURCE_LABEL_KEY[c.sourceType]}`)}
                                      </span>
                                      <span className="text-muted-foreground/40 text-[11px]">
                                        ·
                                      </span>
                                      <span className="truncate text-sm font-medium">
                                        {c.title || tr('untitled')}
                                      </span>
                                    </div>
                                    <p className="text-muted-foreground mt-1 line-clamp-2 text-xs leading-relaxed">
                                      {c.snippet}
                                    </p>
                                  </div>
                                </div>
                              </button>
                            </li>
                          )
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
